import React from 'react';
import TableCoreComponent from './../../reusable/LightTable/TableCoreComponent';
import { PermissionContext, withPermission } from './../../context';
import { userHas, permissions } from "../../permissions";
import {ActionItemsCell, BoldedCell, DateCell, TextCell} from '../../reusable/LightTable/Components';
import { sortBy, toLower } from 'lodash';
import headerActions from './../../reusable/Header/Header.actions';
import api from '../../api/EmailAlerts.api';
import categoriesApi from '../../api/Categories.api';
import { Link, withRouter, hashHistory } from 'react-router';
import withFixedHeader from './../../reusable/LightTable/hoc/withFixedHeader';
import { ConfirmModal, ListingPageHeaderPerm as ListingPageHeader, ActiveLabel } from './../../reusable/LightTable/Components'
import { FormattedMessage } from 'react-intl';
import { ALERTS_PAGE_SIZE, PAGE_NAME_MANAGE_ALERTS, CATEGORIES_ENTITIES_VISIBLE_LABELS } from './../../constants';
import {
    BulkActivateButton,
    BulkPauseButton,
    BulkDeleteButton,
    PauseButton
} from './../../reusable/Form/Buttons';
import withPagination from './../../reusable/LightTable/hoc/Paginated';
import withSort from './../../reusable/LightTable/hoc/Sortable';
import withSearch from './../../reusable/LightTable/hoc/Searchable';
import withSelectionPaginated from './../../reusable/LightTable/hoc/SelectablePaginated';
import LightTable from './../../reusable/LightTable/VeryLightTable';
import { StaticPillList } from '../../reusable/Form/PillList';
import { compose } from 'redux';
import reduxStore from '../../reduxStore';

const Table = compose(
    withPermission,
    withSelectionPaginated,
    withSort,
    withSearch,
    withPagination,
    withFixedHeader
)(LightTable);

class EmailAlertsPage extends TableCoreComponent {
    constructor(props) {
        super(props);

        const defaultLoadParams = {
            filters:    {query: ""},
            sorting:    reduxStore.getState().user.preferences.pagesTablesSort[PAGE_NAME_MANAGE_ALERTS],
            pagination: {pageNumber: 0, pageSize: ALERTS_PAGE_SIZE},
            reset:      false
        };
        this.defaultLoadParams = this.loadParams = defaultLoadParams;
        this.api = api;

        this.handleBulkActivate = this.handleBulkActivate.bind(this);
        this.handleBulkPause = this.handleBulkPause.bind(this);
        this.handleToggleActivateAction = this.handleToggleActivateAction.bind(this);
        this.handleBulkDeleteConfirm = this.handleBulkDeleteConfirm.bind(this);
        this.handleAddNewAlert = this.handleAddNewAlert.bind(this);
        this.formatItem = this.formatItem.bind(this);
    }

    componentDidMount() {
        categoriesApi.getCategoryNames().then(categories => {
            this.setState({ categories }, () => {
                super.componentDidMount();
            });
        }).catch(this.fatalError);
        headerActions.updateHeader('manageEmailAlerts');
    }

    // Table columns with their keys
    headerItems = [
        {
            key:      "name",
            label:    <FormattedMessage id={"Alerts_Create_UIText_name.step1.title"} />,
            sortable: true,
            persistableSort: PAGE_NAME_MANAGE_ALERTS
        },
        {
            key:      "categoriesEntities",
            label:    <FormattedMessage id={"RSS_Feed_UIText_feedListing.inclusion"} />
        },
        {
            key:   "frequency",
            label: <FormattedMessage id={"Alerts_Manage_UIText_frequency"} />
        },
        {
            key:      "active",
            label:    <FormattedMessage id={"Entities_Edit_UIText_editThirdParty.statusLabel"} />,
            sortable: true,
            persistableSort: PAGE_NAME_MANAGE_ALERTS
        },
        {
            key:   "recipients",
            label: <FormattedMessage id={"Alerts_Manage_UIText_recipients"} />
        },
        {
            key:      "createdDate",
            label:    <FormattedMessage id={"General_CoreFunctionality_UIText_createdDate"} />,
            sortable: true,
            persistableSort: PAGE_NAME_MANAGE_ALERTS
        },
        {
            key:   "actions",
            label: ""
        }
    ];

    /**
     * Transforms the list item object to the format for Selectable/Scrollable/LightTable, keeps in 'object' key the original item
     * @param item
     * @param searchTerm - used to bold the name when searching
     * @returns {{key: (types.uuid|{check, operator}|*), name: XML, recipients: (recipientData|{emails, deliveryType, attachType}), frequency: (scheduleMeta|{frequency}|XML), active: XML, createdDate: *, actions: XML, object: *}}
     */
    formatItem(item, searchTerm = "") {
        return {
            key: item.id,
            categoriesEntities: this.formatCatsEnts(item),
            name: <BoldedCell value={item.name} searchTerm={searchTerm} />,
            recipients: <TextCell>{item.recipientData && item.recipientData.emails.join(", ")}</TextCell>,
            frequency: <TextCell>
                {item.scheduleMeta &&
                    <FormattedMessage id={"Alerts_Create_UIText_steps.step2." + toLower(item.scheduleMeta.frequency)}/>}
                </TextCell>,
            active: <TextCell><ActiveLabel isActive={item.active}/></TextCell>,
            createdDate: <DateCell timestamp={item.createdDate}/>,
            actions: <ActionItemsCell>
                <PauseButton action={(e) => this.handleToggleActivateAction(item)} item={item} />
                <Link to={"/email-alerts/" + item.id} className={'action-btn'}>
                    <FormattedMessage id="General_CoreFunctionality_UIText_general.edit"/>
                </Link>
            </ActionItemsCell>,
            object: item
        }
    }

    formatCatsEnts(item) {
        let items, className = "";
        if (item.alertFilterMeta && item.alertFilterMeta.selectAllThirdParties) {
            items = [{ label: <FormattedMessage id="RSS_Feed_UIText_feedListing.allActive" /> }];
        } else if (item.alertFilterMeta && item.alertFilterMeta.categories && item.alertFilterMeta.categories.length > 0) {
            className = "categories";
            if (this.state.categories) {
                items = item.alertFilterMeta.categories.map(catId =>
                    this.state.categories[catId] ? ({ label: this.state.categories[catId] }) : false
                );
                if (items.filter(item => item).length === 0) { // if no valid category, it is orphaned
                    return <TextCell className={"inactive-alert"}><FormattedMessage id="Alerts_Manage_UIText_orphanedAlert" /></TextCell>;
                }
            } else {
                items = [];
            }
        } else if (item.alertFilterMeta && item.alertFilterMeta.thirdParties && item.alertFilterMeta.thirdParties.length > 0) {
            className = "entities";
            items = item.alertFilterMeta.thirdParties.map(entityId => {return {label: entityId}})
        } else {
            return <TextCell className={"inactive-alert"}><FormattedMessage id="Alerts_Manage_UIText_orphanedAlert" /></TextCell>;
        }

        return <StaticPillList items={items}
                               className={className}
                               visibleItemCount={CATEGORIES_ENTITIES_VISIBLE_LABELS}
                               onShowMore={() => {hashHistory.push("/email-alerts/" + item.id)}} />

    }

    handleAddNewAlert() {
        this.props.router.push('/create-new-alert')
    }

    /**
     * If item is not busy, toggle the active flag until successful response, otherwise change the active flag back to
     * previous state
     * @param item
     */
    handleToggleActivateAction(item) {
        if (item.busy) return;

        this.setItemLoading(item, true);

        try {
            if (item.active) {
                api.pauseAlerts([item.id]).then(() => {
                    this.updateItem({...item, active: false});
                    this.setItemLoading(item, false);
                })
            } else {
                api.activateAlerts([item.id]).then(() => {
                    this.updateItem({...item, active: true});
                    this.setItemLoading(item, false);
                })
            }
        }
        catch (e) {
            this.setItemLoading(item, false);
            this.showGenericError(e)
        }
    }

    handleBulkActivate() {
        const pausedItems = this.state.selectedItems.filter(item => !item.object.active).map(item => item.object)

        this.setItemsLoading(pausedItems, true);

        api.activateAlerts(pausedItems.map(item => item.id)).then(() => {
            this.updateItems(pausedItems.map(item => ({ ...item, active: true})));
            this.setItemsLoading(pausedItems, false);
        }).catch((e) => {
            this.setItemsLoading(pausedItems, false);
            this.showGenericError(e)
        })
    }

    handleBulkPause() {
        const activeItems = this.state.selectedItems.filter(item => item.object.active).map(item => item.object)

        this.setItemsLoading(activeItems, true);

        api.pauseAlerts(activeItems.map(item => item.id)).then(() => {
            const updatedActiveItems = activeItems.map(item => ({ ...item, active: false}));
            this.updateItems(updatedActiveItems);
            this.setItemsLoading(updatedActiveItems, false);
        }).catch((e) => {
            this.setItemsLoading(activeItems, false);
            this.showGenericError(e)
        })
    }

    handleBulkDeleteConfirm() {
        const activeModal = <ConfirmModal headerTextId="Alerts_Manage_UIText_deleteAlertHeader"
                                          primaryTextId="Alerts_Manage_UIText_deleteAlertText1"
                                          secondaryTextId="Alerts_Manage_UIText_deleteAlertText2"
                                          onClose={this.closeModal}
                                          onConfirm={() => {

                                              this.clearSelections();
                                          }}
                                          onConfirmPromise={() => this.deleteAlerts(this.state.selectedItems.map(item => item.object))}/>;
        this.setState({ activeModal })
    }

    deleteAlerts(alerts) {
        const alertIds = alerts.map(item => item.id);

        return api.deleteAlerts(alertIds).then(() => {
            this.reloadTable();
        }).catch((e) => {
            this.showGenericError(e, true);
        });
    }

    render() {
        const buttons = <div className="table-buttons">
            <BulkActivateButton action={this.handleBulkActivate} disabled={this.state.selectedItems.filter(item => !item.object.active).length === 0}/>
            <BulkPauseButton action={this.handleBulkPause} disabled={this.state.selectedItems.filter(item => item.object.active).length === 0}/>
            <BulkDeleteButton action={this.handleBulkDeleteConfirm} disabled={this.state.selectedItems.length === 0}/>
        </div>;

        return (
            <PermissionContext.Provider value={{ canEdit: userHas(permissions.alerts.edit) }}>
                <div className="manage-email-alerts listing-page standard-table">
                    <ListingPageHeader location={"/create-new-alert"} buttonMessageId="Alerts_Manage_UIText_createAlert" />
                    <div className="selectable-table email-alerts-table">
                        <Table {...this.getTableProps()}
                               selectedItems={sortBy(this.state.selectedItems, item => item.name.props.value.toLowerCase())}
                               actions={buttons}
                               fullScreen/>
                    </div>
                    {this.state.activeModal && this.state.activeModal}
                </div>
            </PermissionContext.Provider>
        );
    }
}

export default withRouter(EmailAlertsPage);


// WEBPACK FOOTER //
// ./src/scripts/pages/EmailAlerts/EmailAlertsPage.jsx